import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Search, Package, MapPin, Clock, CreditCard, AlertTriangle, CheckCircle2, Download, Eye } from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatEuro } from "@/lib/catalog-format";
import { toIncl, vatOf } from "@/lib/totals";
import { downloadOrderPdf, generateOrderPdf, DEFAULT_PAYMENT_TERM_DAYS, type OrderForPdf } from "@/lib/order-pdf";
import { lookupOrderStatus, type OrderStatusResult } from "@/lib/order-status.functions";
import { PdfPreviewDialog } from "@/components/PdfPreviewDialog";

export const Route = createFileRoute("/order-status")({
  head: () => ({
    meta: [
      { title: "Bestelstatus bekijken — Elvion Group Nederland" },
      {
        name: "description",
        content:
          "Bekijk de status en afleverinformatie van je bestelling met je ordernummer en e-mailadres.",
      },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: OrderStatusPage,
});

const STATUS_LABEL: Record<string, string> = {
  pending: "In behandeling",
  paid: "Betaald",
  processing: "Wordt verwerkt",
  shipped: "Verzonden",
  delivered: "Bezorgd",
  cancelled: "Geannuleerd",
  completed: "Afgerond",
};

const PAY_LABEL: Record<string, string> = {
  open: "Wacht op betaling",
  pending: "Betaling in behandeling",
  authorized: "Geautoriseerd",
  paid: "Betaald",
  failed: "Mislukt",
  expired: "Verlopen",
  canceled: "Geannuleerd",
};

const MOLLIE_FAILURE_NL: Record<string, string> = {
  insufficient_funds: "Onvoldoende saldo op de rekening.",
  invalid_card_number: "Ongeldig kaartnummer.",
  invalid_cvv: "Ongeldige CVC.",
  card_expired: "De kaart is verlopen.",
  card_declined: "De kaart is geweigerd door de bank.",
  authentication_failed: "Authenticatie (3-D Secure) mislukt.",
  authentication_abandoned: "Authenticatie afgebroken.",
  possible_fraud: "Geweigerd vanwege mogelijk frauderisico.",
  unknown_reason: "Onbekende reden – probeer het opnieuw of kies een andere methode.",
};

const METHOD_LABEL: Record<string, string> = {
  ideal: "iDEAL",
  creditcard: "Creditcard",
  bancontact: "Bancontact",
  applepay: "Apple Pay",
  banktransfer: "Bankoverschrijving",
  sofort: "SOFORT Banking",
  przelewy24: "Przelewy24",
  belfius: "Belfius",
  kbc: "KBC/CBC",
  eps: "EPS",
  giropay: "Giropay",
};
function methodLabel(m: string) {
  return METHOD_LABEL[m] ?? m;
}

function statusLabel(s: string) {
  return STATUS_LABEL[s] ?? s;
}
function payLabel(s: string) {
  return PAY_LABEL[s] ?? s;
}
function payTone(s: string): "ok" | "warn" | "bad" | "neutral" {
  if (s === "paid") return "ok";
  if (s === "failed" || s === "expired" || s === "canceled") return "bad";
  if (s === "pending" || s === "authorized" || s === "open") return "warn";
  return "neutral";
}

function OrderStatusPage() {
  const lookup = useServerFn(lookupOrderStatus);
  const [form, setForm] = useState({ orderNumber: "", transactionId: "", email: "" });
  const [mode, setMode] = useState<"order" | "transaction">("order");
  const [result, setResult] = useState<OrderStatusResult | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const orderForPdf = (r: OrderStatusResult): OrderForPdf => ({
    order_number: r.order_number,
    status: r.status,
    total_cents: r.total_cents,
    created_at: r.created_at,
    line_items: r.line_items,
    shipping_address: r.shipping_address ?? null,
  });

  const mutation = useMutation({
    mutationFn: () =>
      lookup({
        data: {
          orderNumber: mode === "order" ? form.orderNumber.trim() || undefined : undefined,
          transactionId: mode === "transaction" ? form.transactionId.trim() || undefined : undefined,
          email: form.email.trim(),
        },
      }),
    onSuccess: (data) => setResult(data),
    onError: () => setResult(null),
  });

  return (
    <>
      <Header />
      <main className="bg-background">
        <section className="mx-auto max-w-3xl px-4 py-12 sm:px-6 lg:py-16">
          <div className="mb-8">
            <h1 className="font-display text-3xl font-extrabold tracking-tight sm:text-4xl">
              Bestelstatus bekijken
            </h1>
            <p className="mt-2 text-text-secondary">
              Zoek je bestelling op met je <strong>ordernummer</strong> óf je <strong>transactie-ID</strong> van de betaling, samen met het <strong>e-mailadres</strong> waarmee je hebt besteld.
            </p>

          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              mutation.mutate();
            }}
            className="rounded-2xl border border-border bg-card p-6 shadow-sm"
          >
            <div className="mb-4 inline-flex rounded-lg border border-border bg-muted p-1 text-sm">
              <button
                type="button"
                onClick={() => setMode("order")}
                className={`rounded-md px-3 py-1.5 font-medium transition ${mode === "order" ? "bg-background shadow-sm" : "text-text-secondary"}`}
              >
                Ordernummer
              </button>
              <button
                type="button"
                onClick={() => setMode("transaction")}
                className={`rounded-md px-3 py-1.5 font-medium transition ${mode === "transaction" ? "bg-background shadow-sm" : "text-text-secondary"}`}
              >
                Transactie-ID
              </button>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {mode === "order" ? (
                <div>
                  <Label htmlFor="orderNumber">Ordernummer</Label>
                  <p className="text-xs text-text-secondary mb-1">Bijv. ORD-ABC123 (staat in je bevestigingsmail)</p>
                  <Input
                    id="orderNumber"
                    placeholder="ORD-ABC123"
                    value={form.orderNumber}
                    onChange={(e) => setForm({ ...form, orderNumber: e.target.value })}
                    required
                  />
                </div>
              ) : (
                <div>
                  <Label htmlFor="transactionId">Transactie-ID</Label>
                  <p className="text-xs text-text-secondary mb-1">Bijv. pi_… of cs_… (Stripe) of tr_… (Mollie)</p>
                  <Input
                    id="transactionId"
                    placeholder="pi_… / cs_… / tr_…"
                    value={form.transactionId}
                    onChange={(e) => setForm({ ...form, transactionId: e.target.value })}
                    required
                  />
                </div>
              )}
              <div>
                <Label htmlFor="email">E-mailadres</Label>
                <p className="text-xs text-text-secondary mb-1">Het e-mailadres dat je bij het bestellen hebt gebruikt</p>
                <Input
                  id="email"
                  type="email"
                  placeholder="naam@voorbeeld.nl"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                />

              </div>
            </div>
            <Button type="submit" className="mt-5 w-full sm:w-auto" disabled={mutation.isPending}>
              <Search className="mr-2 size-4" />
              {mutation.isPending ? "Zoeken…" : "Bestelling opzoeken"}
            </Button>
            {mutation.isError && (
              <p className="mt-4 text-sm text-red-600">
                {(mutation.error as Error)?.message ??
                  "Geen bestelling gevonden met deze gegevens."}
              </p>
            )}
          </form>

          {result && (
            <article className="mt-8 space-y-6">
              <div className="rounded-2xl border border-border bg-card p-6 shadow-sm">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2 text-sm text-text-secondary">
                      <Package className="size-4" />
                      Ordernummer
                    </div>
                    <h2 className="mt-1 font-display text-xl font-bold">{result.order_number}</h2>
                    <p className="text-xs text-text-secondary">
                      Geplaatst op{" "}
                      {new Date(result.created_at).toLocaleDateString("nl-NL", {
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                      })}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <span className="inline-flex items-center rounded-full bg-orange/10 px-3 py-1 text-sm font-semibold text-orange">
                      {statusLabel(result.status)}
                    </span>
                    <div className="flex flex-wrap items-center gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setPreviewOpen(true)}
                      >
                        <Eye className="mr-2 size-4" />
                        Voorbeeld
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          downloadOrderPdf(orderForPdf(result), DEFAULT_PAYMENT_TERM_DAYS)
                        }
                      >
                        <Download className="mr-2 size-4" />
                        Factuur (PDF)
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="mt-6 mb-2 flex items-baseline justify-between text-[11px] uppercase tracking-wide text-text-secondary">
                  <span>Artikel</span>
                  <span>Prijs (excl. btw)</span>
                </div>
                <ul className="divide-y divide-border">
                  {result.line_items.map((it, i) => (
                    <li key={i} className="flex items-start justify-between gap-3 py-3 text-sm">
                      <div>
                        <p className="font-semibold">{it.name}</p>
                        <p className="text-xs text-text-secondary">Aantal: {it.qty}</p>
                      </div>
                      <div className="text-right">
                        <span className="font-semibold">{formatEuro(it.line_total_cents)}</span>
                        <p className="text-[11px] text-text-secondary">
                          {formatEuro(toIncl(it.line_total_cents))} incl. btw
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
                <div className="mt-3 space-y-1 border-t border-border pt-3 text-sm">
                  <div className="flex items-baseline justify-between text-text-secondary">
                    <span>Subtotaal (excl. btw)</span>
                    <span>{formatEuro(result.total_cents)}</span>
                  </div>
                  <div className="flex items-baseline justify-between text-text-secondary">
                    <span>Btw (21%)</span>
                    <span>{formatEuro(vatOf(result.total_cents))}</span>
                  </div>
                  <div className="flex items-baseline justify-between pt-1 border-t border-border">
                    <span className="font-semibold">Totaal (incl. btw)</span>
                    <span className="text-lg font-bold">{formatEuro(toIncl(result.total_cents))}</span>
                  </div>
                </div>
              </div>

              <PaymentCard result={result} />


              {result.shipping_address && (
                <div className="rounded-2xl border border-border bg-card p-6 shadow-sm">
                  <div className="flex items-center gap-2 text-sm text-text-secondary">
                    <MapPin className="size-4" />
                    Bezorgadres
                  </div>
                  <p className="mt-2 text-sm">
                    {result.shipping_address.street} {result.shipping_address.house_number}
                    <br />
                    {result.shipping_address.postal_code} {result.shipping_address.city}
                    <br />
                    {result.shipping_address.country}
                  </p>
                </div>
              )}

              {result.history.length > 0 && (
                <div className="rounded-2xl border border-border bg-card p-6 shadow-sm">
                  <div className="flex items-center gap-2 text-sm text-text-secondary">
                    <Clock className="size-4" />
                    Statusverloop
                  </div>
                  <ol className="mt-4 space-y-3">
                    {result.history.map((h, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm">
                        <span className="mt-1 inline-block size-2 shrink-0 rounded-full bg-orange" />
                        <div>
                          <p className="font-semibold">{statusLabel(h.to_status)}</p>
                          {h.note && <p className="text-text-secondary">{h.note}</p>}
                          <p className="text-xs text-text-secondary">
                            {new Date(h.created_at).toLocaleString("nl-NL")}
                          </p>
                        </div>
                      </li>
                    ))}
                  </ol>
                </div>
              )}
            </article>
          )}
        </section>
      </main>
      <Footer />
      {result ? (
        <PdfPreviewDialog
          open={previewOpen}
          onOpenChange={setPreviewOpen}
          title={`Factuur ${result.order_number}`}
          description="Voorvertoning — controleer voordat je downloadt"
          fileName={`Factuur-${result.order_number}.pdf`}
          source={{
            kind: "blob",
            getBlobUrl: async () => {
              const doc = await generateOrderPdf(
                orderForPdf(result),
                DEFAULT_PAYMENT_TERM_DAYS,
              );
              return doc.output("bloburl") as unknown as string;
            },
          }}
        />
      ) : null}
    </>
  );
}

function PaymentCard({ result }: { result: OrderStatusResult }) {
  const tone = payTone(result.payment_status);
  const toneClass =
    tone === "ok"
      ? "border-green-200 bg-green-50 text-green-800"
      : tone === "bad"
      ? "border-red-200 bg-red-50 text-red-800"
      : tone === "warn"
      ? "border-amber-200 bg-amber-50 text-amber-900"
      : "border-border bg-card text-foreground";

  const Icon = tone === "ok" ? CheckCircle2 : tone === "bad" ? AlertTriangle : CreditCard;

  // Zelfde afronding als checkout/Mollie: `incl = ex + round(ex*rate)`.
  const totalIncl = result.total_cents + Math.round(result.total_cents * 0.21);
  const isPickup = result.fulfillment_method === "pickup";
  const remaining = Math.max(totalIncl - result.amount_paid_cents, 0);

  const failureKey =
    result.mollie?.failureReason && MOLLIE_FAILURE_NL[result.mollie.failureReason]
      ? MOLLIE_FAILURE_NL[result.mollie.failureReason]
      : result.mollie?.failureReason ?? null;

  return (
    <div className={`rounded-2xl border p-6 shadow-sm ${toneClass}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 text-sm font-semibold">
          <Icon className="size-4" />
          Betaalstatus
        </div>
        <span className="inline-flex items-center rounded-full bg-white/70 px-3 py-1 text-xs font-semibold">
          {payLabel(result.payment_status)}
        </span>
      </div>

      <dl className="mt-4 grid gap-2 text-sm sm:grid-cols-2">
        <div>
          <dt className="text-text-secondary">Bezorgmethode</dt>
          <dd className="font-semibold">
            {isPickup ? "Afhalen in Wageningen" : "Bezorgen"}
          </dd>
        </div>
        <div>
          <dt className="text-text-secondary">Totaal (incl. btw)</dt>
          <dd className="font-semibold">{formatEuro(totalIncl)}</dd>
        </div>
        {isPickup && (
          <div>
            <dt className="text-text-secondary">Aanbetaling</dt>
            <dd className="font-semibold">{formatEuro(result.deposit_cents)}</dd>
          </div>
        )}
        <div>
          <dt className="text-text-secondary">Reeds betaald</dt>
          <dd className="font-semibold">{formatEuro(result.amount_paid_cents)}</dd>
        </div>
        {isPickup && result.amount_paid_cents > 0 && (
          <div className="sm:col-span-2">
            <dt className="text-text-secondary">Bij afhalen voldoen</dt>
            <dd className="font-semibold">{formatEuro(remaining)}</dd>
          </div>
        )}
        {result.mollie?.method && (
          <div>
            <dt className="text-text-secondary">Betaalmethode</dt>
            <dd className="font-semibold">{methodLabel(result.mollie.method)}</dd>
          </div>
        )}
        {result.mollie_payment_id && (
          <div className="sm:col-span-2">
            <dt className="text-text-secondary">Transactie-ID (Mollie)</dt>
            <dd className="font-mono text-xs font-semibold break-all">
              {result.mollie_payment_id}
            </dd>
          </div>
        )}
        {result.mollie?.paidAt && (
          <div>
            <dt className="text-text-secondary">Betaald op</dt>
            <dd className="font-semibold">
              {new Date(result.mollie.paidAt).toLocaleString("nl-NL")}
            </dd>
          </div>
        )}
      </dl>

      {result.mollie_payment_id && (
        <p className="mt-4 text-xs text-text-secondary">
          Verifieer je betaling aan de hand van bovenstaand transactie-ID op je bankafschrift of
          bewaar het voor je administratie.
        </p>
      )}

      {failureKey && (
        <div className="mt-4 rounded-lg bg-white/70 p-3 text-sm">
          <p className="font-semibold">Foutmelding van Mollie</p>
          <p className="mt-1">{failureKey}</p>
        </div>
      )}

      {(result.payment_status === "failed" ||
        result.payment_status === "expired" ||
        result.payment_status === "canceled" ||
        result.payment_status === "open") && (
        <p className="mt-4 text-xs">
          Betaling niet gelukt of nog niet voltooid? Neem contact op via{" "}
          <a href="mailto:info@elvion-group.nl" className="font-semibold underline">
            info@elvion-group.nl
          </a>{" "}
          dan helpen we je verder.
        </p>
      )}
    </div>
  );
}

